
// AIA Materials Pledge Category Mapping
// Maps MasterFormat section numbers to AIA category names
export interface AIACategoryRule {
    category: string;
    masterFormatSection: string;
    matchRule: 'startsWith' | 'exact';
    priority: number; // Higher priority = more specific match
}

export const AIACategoryRules: AIACategoryRule[] = [
    // Exact matches (highest priority - 100)
    {
        category: 'Gypsum board assemblies',
        masterFormatSection: '09 21 16',
        matchRule: 'exact',
        priority: 100,
    },
    {
        category: 'Metal wall panels',
        masterFormatSection: '07 42 13',
        matchRule: 'exact',
        priority: 100,
    },
    {
        category: 'Hollow metal doors and frames',
        masterFormatSection: '08 11 13',
        matchRule: 'exact',
        priority: 100,
    },
    {
        category: 'Flush wood doors',
        masterFormatSection: '08 14 16',
        matchRule: 'exact',
        priority: 100,
    },
    {
        category: 'Countertops',
        masterFormatSection: '12 36 00',
        matchRule: 'exact',
        priority: 100,
    },
    {
        category: 'Acoustical ceilings',
        masterFormatSection: '09 83 16',
        matchRule: 'exact',
        priority: 100,
    },
    {
        category: 'Non-structural metal framing',
        masterFormatSection: '09 22 16',
        matchRule: 'exact',
        priority: 100,
    },
    
    // Multiple exact matches for Mass timber (priority 95)
    {
        category: 'Mass timber',
        masterFormatSection: '06 15 00',
        matchRule: 'exact',
        priority: 95,
    },
    {
        category: 'Mass timber',
        masterFormatSection: '06 18 00',
        matchRule: 'exact',
        priority: 95,
    },
    
    // Fabric - exact matches for various sections (priority 90)
    {
        category: 'Fabric',
        masterFormatSection: '09 72 16',
        matchRule: 'exact',
        priority: 90,
    },
    {
        category: 'Fabric',
        masterFormatSection: '09 72 19',
        matchRule: 'exact',
        priority: 90,
    },
    {
        category: 'Fabric',
        masterFormatSection: '09 77 13',
        matchRule: 'exact',
        priority: 90,
    },
    {
        category: 'Fabric',
        masterFormatSection: '09 77 23',
        matchRule: 'exact',
        priority: 90,
    },
    {
        category: 'Fabric',
        masterFormatSection: '12 05 13',
        matchRule: 'exact',
        priority: 90,
    },
    
    // Very specific startsWith matches (priority 60)
    {
        category: 'Structural steel',
        masterFormatSection: '05 12',
        matchRule: 'startsWith',
        priority: 60,
    },
    {
        category: 'Countertops',
        masterFormatSection: '12 36',
        matchRule: 'startsWith',
        priority: 60,
    },
    
    // Specific startsWith matches (priority 50)
    {
        category: 'Acoustical ceilings',
        masterFormatSection: '09 51',
        matchRule: 'startsWith',
        priority: 50,
    },
    {
        category: 'Acoustical ceilings',
        masterFormatSection: '09 53',
        matchRule: 'startsWith',
        priority: 50,
    },
    {
        category: 'Resilient flooring',
        masterFormatSection: '09 65',
        matchRule: 'startsWith',
        priority: 50,
    },
    {
        category: 'Carpeting',
        masterFormatSection: '09 68',
        matchRule: 'startsWith',
        priority: 50,
    },
    {
        category: 'Paint and coatings',
        masterFormatSection: '09 9',
        matchRule: 'startsWith',
        priority: 50,
    },
    {
        category: 'Cast-in-place concrete',
        masterFormatSection: '03 3',
        matchRule: 'startsWith',
        priority: 50,
    },
    {
        category: 'Unit masonry',
        masterFormatSection: '04 2',
        matchRule: 'startsWith',
        priority: 50,
    },
    {
        category: 'Stone assemblies',
        masterFormatSection: '04 4',
        matchRule: 'startsWith',
        priority: 50,
    },
    {
        category: 'Rough carpentry',
        masterFormatSection: '06 1',
        matchRule: 'startsWith',
        priority: 50,
    },
    {
        category: 'Finish carpentry',
        masterFormatSection: '06 2',
        matchRule: 'startsWith',
        priority: 50,
    },
    {
        category: 'Glazing',
        masterFormatSection: '08 8',
        matchRule: 'startsWith',
        priority: 50,
    },
    {
        category: 'Non-structural metal framing',
        masterFormatSection: '05 4',
        matchRule: 'startsWith',
        priority: 50,
    },
    {
        category: 'Fabric',
        masterFormatSection: '12 22',
        matchRule: 'startsWith',
        priority: 50,
    },
    
    // Broader startsWith matches (priority 40)
    {
        category: 'Insulation',
        masterFormatSection: '07 2',
        matchRule: 'startsWith',
        priority: 40,
    },
    {
        category: 'Tiling',
        masterFormatSection: '09 3',
        matchRule: 'startsWith',
        priority: 40,
    },
    
    // Division-level match (lowest priority - 30)
    {
        category: 'Furniture',
        masterFormatSection: '12',
        matchRule: 'startsWith',
        priority: 30,
    },
];

/**
 * Normalizes a MasterFormat section number by removing extra spaces
 * Example: "09  51  00" -> "09 51 00"
 */
export const normalizeSectionNumber = (sectionNumber: string): string => {
    if (!sectionNumber) return '';
    return sectionNumber.trim().replaceAll(/\s+/g, ' ');
};

/**
 * Maps a product's MasterFormat section number to an AIA Materials Pledge category
 * Returns the most specific matching category based on priority
 */
export const getAIACategory = (sectionNumber: string | undefined): string | null => {
    if (!sectionNumber) return null;

    const normalized = normalizeSectionNumber(sectionNumber);
    
    // Find all matching rules
    const matches = AIACategoryRules.filter(rule => {
        if (rule.matchRule === 'exact') {
            return normalized === rule.masterFormatSection || 
                   normalized.startsWith(rule.masterFormatSection + ' ') ||
                   normalized.startsWith(rule.masterFormatSection + '.');
        } else {
            // startsWith
            return normalized.startsWith(rule.masterFormatSection);
        }
    });
    
    // Return the highest priority match
    if (matches.length === 0) return null;
    
    matches.sort((a, b) => b.priority - a.priority);
    return matches[0].category;
};

/**
 * Gets the MasterFormat section number from a product
 * Checks multiple fields in order of specificity
 */
export const getProductSectionNumber = (product: any): string | null => {
    return product?.mfSection?.number ||
           product?.mfSectionLevel3?.number ||
           product?.mfSectionLevel2?.number ||
           product?.mfSectionLevel1?.number ||
           null;
};

/**
 * Extracts MF division code from a section number
 * Includes third number if it exists and is not "00"
 * Example: "04 41 00" -> "04 41"
 * Example: "04 41 13" -> "04 41 13"
 * Example: "04 4" -> "04 4"
 * Example: "12 36 00" -> "12 36"
 */
export const extractMfDivisionCode = (sectionNumber: string): string => {
    const normalized = normalizeSectionNumber(sectionNumber);
    const parts = normalized.split(' ').filter(Boolean);

    // If we have at least 2 parts, check for third part
    if (parts.length >= 2) {
        // Check if there's a third part and if it's not "00"
        if (parts.length >= 3 && parts[2] !== '00') {
            return `${parts[0]} ${parts[1]} ${parts[2]}`;
        }
        // Return first two parts
        return `${parts[0]} ${parts[1]}`;
    }
    
    // Otherwise return what we have
    return normalized;
};

/**
 * Gets all unique MF division codes for products in an AIA category
 * Includes third number if it exists and is not "00"
 * Returns sorted, comma-separated string like "04 41, 04 45 13, 04 47"
 */
export const getMfDivisionsForProducts = (products: any[]): string => {
    const divisionCodes = new Set<string>();
    
    products.forEach(product => {
        const sectionNumber = getProductSectionNumber(product);
        if (sectionNumber) {
            const divisionCode = extractMfDivisionCode(sectionNumber);
            divisionCodes.add(divisionCode);
        }
    });
    
    // Sort and join with commas
    return Array.from(divisionCodes)
        .sort((a, b) => a.localeCompare(b))
        .join(', ');
};

// AIA Table Row interface
export interface AIATableRow {
    key: string;
    aiaCategory: string;
    mfDivisions: string;
    totalProducts: number;
    products: any[]; // Store products for transparency/documents calculation
}

/**
 * Transforms products into AIA Materials Pledge table rows
 * Groups by AIA category, calculates MF divisions and counts
 */
export const buildAIATableData = (products: any[]): AIATableRow[] => {
    // Group products by AIA category
    const categoryMap = new Map<string, any[]>();
    let counter = 0;
    products.forEach(product => {
        const sectionNumber = getProductSectionNumber(product);
        const aiaCategory = getAIACategory(sectionNumber || undefined);
        
        if (aiaCategory) {
            if (aiaCategory.toLowerCase() === 'insulation') {
                counter++;
            }
            
            if (!categoryMap.has(aiaCategory)) {
                categoryMap.set(aiaCategory, []);
            }
            categoryMap.get(aiaCategory)!.push(product);
        }
    });
    
    // Convert to table rows
    const rows: AIATableRow[] = [];
    categoryMap.forEach((categoryProducts, aiaCategory) => {
        rows.push({
            key: aiaCategory,
            aiaCategory,
            mfDivisions: getMfDivisionsForProducts(categoryProducts),
            totalProducts: categoryProducts.length,
            products: categoryProducts,
        });
    });
    
    // Sort by MF Divisions in ascending order (by first division code)
    rows.sort((a, b) => {
        // Extract first MF division code from comma-separated list
        const firstDivisionA = a.mfDivisions.split(',')[0].trim();
        const firstDivisionB = b.mfDivisions.split(',')[0].trim();
        return firstDivisionA.localeCompare(firstDivisionB);
    });
    
    return rows;
};

export const AIAProductCategoriesMap = new Map